import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Refresh,
  PersonAdd,
  AdminPanelSettings,
} from '@mui/icons-material';
import { accountAPI, Application } from '../../services/api';

const AdminReviewPage: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: 'approve' | 'reject' | null;
    applicationId: number | null;
  }>({
    open: false,
    title: '',
    message: '',
    action: null,
    applicationId: null
  });

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await accountAPI.getApplications();
      setApplications(data);
    } catch (err: any) {
      setError(err.response?.data?.message || '獲取申請列表失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleReview = (application: Application) => {
    setSelectedApplication(application);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedApplication) return;
    
    try {
      await accountAPI.approveApplication(selectedApplication.id);
      
      // 更新本地狀態
      setApplications(prev => 
        prev.map(app => 
          app.id === selectedApplication.id 
            ? { ...app, status: 'approved' as const }
            : app
        )
      );
      
      setReviewDialogOpen(false);
      setSelectedApplication(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '批准申請失敗');
    }
  };

  const handleReject = async () => {
    if (!selectedApplication) return;
    
    try {
      await accountAPI.rejectApplication(selectedApplication.id, reviewNotes);
      
      // 更新本地狀態
      setApplications(prev => 
        prev.map(app => 
          app.id === selectedApplication.id 
            ? { ...app, status: 'rejected' as const }
            : app
        )
      );
      
      setReviewDialogOpen(false);
      setSelectedApplication(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '拒絕申請失敗');
    }
  };

  const handleApproveApplication = async (applicationId: number) => {
    try {
      await accountAPI.approveApplication(applicationId);
      
      // 更新本地狀態
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? { ...app, status: 'approved' as const }
            : app
        )
      );
      
      // 顯示成功提醒
      alert('申請已成功批准！');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || '批准申請失敗';
      setError(errorMessage);
      alert(`批准失敗：${errorMessage}`);
    }
  };

  const handleRejectApplication = async (applicationId: number) => {
    try {
      await accountAPI.rejectApplication(applicationId, '');
      
      // 更新本地狀態
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? { ...app, status: 'rejected' as const }
            : app
        )
      );
      
      // 顯示成功提醒
      alert('申請已成功拒絕！');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || '拒絕申請失敗';
      setError(errorMessage);
      alert(`拒絕失敗：${errorMessage}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待審核';
      case 'approved': return '已批准';
      case 'rejected': return '已拒絕';
      default: return status;
    }
  };

  const filteredApplications = applications.filter(app => {
    if (currentTab === 0) return app.status === 'pending';
    if (currentTab === 1) return app.status === 'rejected';
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Tabs 
                value={currentTab} 
                onChange={handleTabChange} 
                aria-label="application tabs"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    minHeight: '48px',
                    textTransform: 'none',
                    letterSpacing: 'normal'
                  }
                }}
              >
                <Tab 
                  label="待審核" 
                  sx={{ 
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }} 
                />
                <Tab 
                  label="已拒絕" 
                  sx={{ 
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }} 
                />
              </Tabs>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchApplications}
                  sx={{ mr: 1 }}
                >
                  刷新
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: 1.5 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Table sx={{ 
                  borderCollapse: 'separate', 
                  borderSpacing: 0,
                  tableLayout: 'fixed',
                  width: '100%'
                }}>
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: '#1976d2',
                      '& .MuiTableCell-root': {
                        borderBottom: '2px solid #1565c0',
                        borderRight: '1px solid rgba(255,255,255,0.2)',
                        '&:last-child': {
                          borderRight: 'none'
                        }
                      }
                    }}>
                      <TableCell sx={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: '0.95rem',
                        py: 2,
                        width: '18%'
                      }}>申請人</TableCell>
                      <TableCell sx={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: '0.95rem',
                        py: 2,
                        width: '18%'
                      }}>手機號碼</TableCell>
                      <TableCell sx={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: '0.95rem',
                        py: 2,
                        width: '25%'
                      }}>電子郵件</TableCell>
                      <TableCell sx={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: '0.95rem',
                        py: 2,
                        width: '15%'
                      }}>申請所屬部門</TableCell>
                      <TableCell sx={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: '0.95rem',
                        py: 2,
                        width: '20%'
                      }}>申請時間</TableCell>
                      <TableCell sx={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: '0.95rem',
                        py: 2,
                        width: '12%'
                      }}>操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredApplications.length === 0 ? (
                      <TableRow>
                        <TableCell 
                          colSpan={6} 
                          sx={{ 
                            textAlign: 'center', 
                            py: 8,
                            borderBottom: 'none'
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            color: 'text.secondary'
                          }}>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'normal' }}>
                              目前沒有資料
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {currentTab === 0 ? '暫無待審核的申請' : '暫無已拒絕的申請'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredApplications.map((application, index) => (
                      <TableRow 
                        key={application.id} 
                        hover
                        sx={{
                          '&:nth-of-type(even)': {
                            backgroundColor: '#fafafa',
                          },
                          '&:hover': {
                            backgroundColor: '#f0f4ff',
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: '1px solid #e0e0e0',
                            borderRight: '1px solid #e0e0e0',
                            '&:last-child': {
                              borderRight: 'none'
                            }
                          }
                        }}
                      >
                        <TableCell sx={{ textAlign: 'center', py: 2, width: '18%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.1rem' }}>
                            {application.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', py: 2, width: '18%' }}>
                          <Typography variant="body1" sx={{ 
                            fontFamily: 'monospace',
                            color: '#1976d2',
                            fontWeight: '500',
                            fontSize: '1.1rem'
                          }}>
                            {application.username}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', py: 2, width: '25%' }}>
                          <Typography variant="body1" sx={{ color: '#666', fontSize: '1.1rem' }}>
                            {application.email}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', py: 2, width: '15%' }}>
                          <Typography variant="body1" sx={{ 
                            color: '#333',
                            fontWeight: '500',
                            fontSize: '1.1rem'
                          }}>
                            {application.department_name || `部門 ${application.department_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', py: 2, width: '20%' }}>
                          <Typography variant="body1" sx={{ 
                            color: '#666',
                            fontSize: '1.1rem'
                          }}>
                            {new Date(application.datetime).toLocaleString('zh-TW')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', py: 2, width: '12%' }}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {application.status === 'pending' && (
                              <>
                                <Tooltip title="批准" arrow>
                                  <IconButton
                                    size="medium"
                                    onClick={() => {
                                      setConfirmDialog({
                                        open: true,
                                        title: '確認批准',
                                        message: `確定要批准 ${application.name} 的申請嗎？`,
                                        action: 'approve',
                                        applicationId: application.id
                                      });
                                    }}
                                    sx={{ 
                                      color: 'white',
                                      backgroundColor: 'success.main',
                                      '&:hover': {
                                        backgroundColor: 'success.dark'
                                      }
                                    }}
                                  >
                                    <CheckCircle fontSize="medium" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="拒絕" arrow>
                                  <IconButton
                                    size="medium"
                                    onClick={() => {
                                      setConfirmDialog({
                                        open: true,
                                        title: '確認拒絕',
                                        message: `確定要拒絕 ${application.name} 的申請嗎？`,
                                        action: 'reject',
                                        applicationId: application.id
                                      });
                                    }}
                                    sx={{ 
                                      color: 'white',
                                      backgroundColor: 'error.main',
                                      '&:hover': {
                                        backgroundColor: 'error.dark'
                                      }
                                    }}
                                  >
                                    <Cancel fontSize="medium" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 審核對話框 */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonAdd sx={{ mr: 1 }} />
            審核申請
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      申請人姓名
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedApplication.name}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      手機號碼
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedApplication.username}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      電子郵件
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.email}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      部門
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.department_name || `部門 ${selectedApplication.department_id}`}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    申請時間
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedApplication.datetime).toLocaleString('zh-TW')}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="審核備註"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="請輸入審核意見或備註..."
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>
            取消
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="outlined"
            startIcon={<Cancel />}
          >
            拒絕申請
          </Button>
          <Button
            onClick={handleApprove}
            color="success"
            variant="contained"
            startIcon={<CheckCircle />}
          >
            批准申請
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認對話框 */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={() => setConfirmDialog({...confirmDialog, open: false})}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({...confirmDialog, open: false})}
            color="inherit"
          >
            取消
          </Button>
          <Button 
            onClick={() => {
              if (confirmDialog.action === 'approve' && confirmDialog.applicationId) {
                handleApproveApplication(confirmDialog.applicationId);
              } else if (confirmDialog.action === 'reject' && confirmDialog.applicationId) {
                handleRejectApplication(confirmDialog.applicationId);
              }
              setConfirmDialog({...confirmDialog, open: false});
            }}
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            variant="contained"
          >
            確認
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminReviewPage;
